import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { formatNumber, numberWithCommas } from './utils';
import styles from './App.module.css';
import HomePage from './pages/HomePage';
import DifficultiesPage from './pages/DifficultiesPage';
import BlocksPage from './pages/BlocksPage';
import AlgosPage from './pages/AlgosPage';
import PoolsPage from './pages/PoolsPage';
import SupplyPage from './pages/SupplyPage';
import DownloadsPage from './pages/DownloadsPage';
import NodesPage from './pages/NodesPage';
import MainnetLayout from './components/MainnetLayout';
import TestnetLayout from './components/TestnetLayout';
import HashratePage from './pages/HashratePage';
import TaprootPage from './pages/TaprootPage';
import TxsPage from './pages/TxsPage';
import RoadmapPage from './pages/RoadmapPage';
import DigiDollarPage from './pages/DigiDollarPage';
import OraclesPage from './pages/OraclesPage';
import DDStatsPage from './pages/DDStatsPage';
import DDActivationPage from './pages/DDActivationPage';
import WalletConvertPage from './pages/WalletConvertPage';
import PoolUpgradeTrackerPage from './pages/PoolUpgradeTrackerPage';
import ChainTipsPage from './pages/ChainTipsPage';
import NotFoundPage from './pages/NotFoundPage';

// Create a custom theme with DigiByte colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#002352',
      light: '#0066cc',
      dark: '#001c41',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0066cc',
      light: '#4395ff',
      dark: '#003b99',
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

const App = () => {
  const worldPopulation = 8100000000; // Assuming a world population of 8.1 billion

  // No app-wide REST polling (SLOW-10): Supply, Difficulty and Hashrate fetch
  // their own data and get live updates over their page's WebSocket.
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <div className={styles.app}>
          <Routes>
            {/* Mainnet routes */}
            <Route element={<MainnetLayout />}>
              <Route
                index
                path="/"
                element={
                  <HomePage
                    numberWithCommas={numberWithCommas}
                    formatNumber={formatNumber}
                  />
                }
              />
              <Route path="/blocks" element={<BlocksPage />} />
              <Route path="/tips" element={<ChainTipsPage />} />
              <Route path="/txs" element={<TxsPage />} />
              <Route path="/downloads" element={<DownloadsPage />} />
              <Route path="/roadmap" element={<RoadmapPage />} />
              <Route path="/digidollar" element={<DigiDollarPage />} />
              <Route path="/activation" element={<DDActivationPage />} />
              <Route path="/oracles" element={<OraclesPage />} />
              <Route path="/ddstats" element={<DDStatsPage />} />
              <Route path="/nodes" element={<NodesPage />} />
              <Route path="/pools" element={<PoolsPage />} />
              <Route path="/pool-upgrades" element={<PoolUpgradeTrackerPage />} />
              <Route path="/supply" element={<SupplyPage worldPopulation={worldPopulation} />} />
              <Route path="/algos" element={<AlgosPage />} />
              <Route path="/difficulties" element={<DifficultiesPage />} />
              <Route path="/hashrate" element={<HashratePage />} />
              <Route path="/taproot" element={<TaprootPage />} />
              {/* Unknown routes: "Page not found" (usePageSeo sets noindex) */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Testnet routes */}
            <Route path="/testnet" element={<TestnetLayout />}>
              <Route
                index
                element={
                  <HomePage
                    numberWithCommas={numberWithCommas}
                    formatNumber={formatNumber}
                  />
                }
              />
              <Route path="blocks" element={<BlocksPage />} />
              <Route path="tips" element={<ChainTipsPage />} />
              <Route path="txs" element={<TxsPage />} />
              <Route path="supply" element={<SupplyPage worldPopulation={worldPopulation} />} />
              <Route path="algos" element={<AlgosPage />} />
              <Route path="difficulties" element={<DifficultiesPage />} />
              <Route path="hashrate" element={<HashratePage />} />
              <Route path="nodes" element={<NodesPage />} />
              <Route path="pool-upgrades" element={<PoolUpgradeTrackerPage />} />
              <Route path="activation" element={<DDActivationPage />} />
              <Route path="oracles" element={<OraclesPage />} />
              <Route path="ddstats" element={<DDStatsPage />} />
              <Route path="digidollar" element={<DigiDollarPage />} />
              <Route path="convert" element={<WalletConvertPage />} />
              <Route path="taproot" element={<TaprootPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </div>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
